// (2026-05-23) Vista HTML responsive de Comprobante de Retencion publica
// (IVA o ISLR). Maneja ambos tipos con un solo componente parametrizado
// por `type`. Los shapes de respuesta son parecidos pero las claves
// difieren (agente_retencion vs empresa, sujeto_retenido vs
// proveedor_beneficiario, etc.) — se normaliza al render.
import React, { useState } from "react";
import { generateWithholdingPDF } from "../utils/pdf/WithholdingPDF/generateWithholdingPDF";

const fmt = (n, d = 2) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString("es-VE", { minimumFractionDigits: d, maximumFractionDigits: d });
};

const PublicWithholdingView = ({ data, type, realId }) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  if (!data) return null;

  const esIVA = type === "RETIVA";
  const titulo = esIVA ? "COMPROBANTE DE RETENCIÓN IVA" : "COMPROBANTE DE RETENCIÓN ISLR";
  const colorBanner = esIVA ? "#0e7490" : "#7c3aed";

  // Normalizar shapes IVA vs ISLR.
  const cabecera   = data.cabecera || {};
  const agente     = data.agente_retencion || data.empresa || {};
  const sujeto     = data.sujeto_retenido || data.proveedor_beneficiario || {};
  const tercero    = data.tercero || null;
  const detalle    = data.detalle || [];
  const totales    = data.totales || {};
  const legal      = data.legal || {};

  const handleDownload = async () => {
    try {
      setDownloading(true); setError("");
      await generateWithholdingPDF(realId || cabecera.qr_token, "download", esIVA ? "IVA" : "ISLR");
    } catch (e) {
      setError(e?.message || "No se pudo generar el PDF.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="pdv-root">
      <div className="pdv-topbar">
        {/* (2026-05-26 v17) URL relativa — mismo dominio que el frontend */}
        <img src="/quantus-logo.png" alt="Quantus Invoice" className="pdv-topbar-logo" />
        <span className="pdv-topbar-by">by Sirius Solutions</span>
      </div>

      <div className="pdv-page">
        <div className="pdv-banner" style={{ background: colorBanner, color: "#fff" }}>
          <div className="pdv-banner-label">{titulo}</div>
          <div className="pdv-banner-numero">{cabecera.numero_comprobante || "—"}</div>
        </div>

        {/* Agente retención + cabecera */}
        <div className="pdv-grid-2">
          <div className="pdv-card">
            <div className="pdv-card-title">AGENTE DE RETENCIÓN</div>
            <div className="pdv-card-body">
              <div className="pdv-strong">{agente.nombre_razon_social || agente.nombre || "—"}</div>
              <div className="pdv-muted">RIF: {agente.rif || "—"}</div>
              {agente.direccion_fiscal && <div className="pdv-small">{agente.direccion_fiscal}</div>}
              {agente.direccion && !agente.direccion_fiscal && <div className="pdv-small">{agente.direccion}</div>}
            </div>
          </div>
          <div className="pdv-card pdv-card-doc" style={{ "--card-color": colorBanner }}>
            <div className="pdv-card-title" style={{ background: colorBanner, color: "#fff" }}>DATOS DEL COMPROBANTE</div>
            <div className="pdv-card-body">
              <div className="pdv-kv"><span>Nº Comprobante</span><strong>{cabecera.numero_comprobante || "—"}</strong></div>
              {/* (2026-07-18 A3/A4) Nº de Control propio del comprobante, destacado
                  en rojo. Se omite si no viene (comprobantes históricos). */}
              {cabecera.numero_control && (
                <div className="pdv-kv"><span>Nº de Control</span><strong style={{ color: "#dc2626" }}>{cabecera.numero_control}</strong></div>
              )}
              <div className="pdv-kv"><span>Fecha emisión</span><strong>{cabecera.fecha_emision || "—"}</strong></div>
              {esIVA && (
                <>
                  <div className="pdv-kv"><span>Período fiscal</span><strong>{cabecera.periodo_fiscal || "—"}</strong></div>
                  <div className="pdv-kv"><span>Año / Mes</span><strong>{cabecera.anio_fiscal || "—"} / {cabecera.mes_fiscal || "—"}</strong></div>
                </>
              )}
              {!esIVA && (
                <>
                  <div className="pdv-kv"><span>Desde</span><strong>{cabecera.periodo_desde || "—"}</strong></div>
                  <div className="pdv-kv"><span>Hasta</span><strong>{cabecera.periodo_hasta || "—"}</strong></div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Sujeto retenido */}
        <div className="pdv-card">
          <div className="pdv-card-title">{esIVA ? "SUJETO RETENIDO" : "PROVEEDOR / BENEFICIARIO"}</div>
          <div className="pdv-card-body pdv-grid-3">
            <div><div className="pdv-muted-label">Razón social</div><div className="pdv-strong">{sujeto.nombre_razon_social || sujeto.nombre || "—"}</div></div>
            <div><div className="pdv-muted-label">RIF</div><div>{sujeto.rif || "—"}</div></div>
            <div className="pdv-col-span-3"><div className="pdv-muted-label">Dirección</div><div>{sujeto.direccion_fiscal || sujeto.direccion || "—"}</div></div>
          </div>
        </div>

        {/* Tercero (si aplica) */}
        {tercero && (
          <div className="pdv-card">
            <div className="pdv-card-title">TERCERO</div>
            <div className="pdv-card-body pdv-grid-3">
              <div><div className="pdv-muted-label">Razón social</div><div className="pdv-strong">{tercero.nombre_empresa || "—"}</div></div>
              <div><div className="pdv-muted-label">RIF</div><div>{tercero.rif || "—"}</div></div>
            </div>
          </div>
        )}

        {/* Detalle */}
        <div className="pdv-card">
          <div className="pdv-card-title">DETALLE</div>
          <div className="pdv-items-desktop">
            <table className="pdv-items-table">
              {esIVA ? (
                <thead>
                  <tr>
                    <th>Nº Factura</th>
                    <th>Nº Control</th>
                    <th>Fecha</th>
                    <th className="pdv-right">Compras + IVA</th>
                    <th className="pdv-right">Base imponible</th>
                    <th className="pdv-right">IVA</th>
                    <th className="pdv-right">IVA retenido</th>
                  </tr>
                </thead>
              ) : (
                <thead>
                  <tr>
                    <th>Nº documento</th>
                    <th>Fecha</th>
                    <th>Concepto</th>
                    <th className="pdv-right">Monto doc.</th>
                    <th className="pdv-right">Base ret.</th>
                    <th className="pdv-right">% Ret.</th>
                    <th className="pdv-right">Impto. retenido</th>
                  </tr>
                </thead>
              )}
              <tbody>
                {detalle.length === 0 && <tr><td colSpan={7} className="pdv-empty">Sin detalle registrado.</td></tr>}
                {detalle.map((d, i) => esIVA ? (
                  <tr key={i}>
                    <td>{d.numero_factura || ""}</td>
                    <td>{d.numero_control || ""}</td>
                    <td>{d.fecha_factura || ""}</td>
                    <td className="pdv-right">{fmt(d.total_compras_con_iva)}</td>
                    <td className="pdv-right">{fmt(d.base_imponible)}</td>
                    <td className="pdv-right">{fmt(d.impuesto_iva)}</td>
                    <td className="pdv-right" style={{ fontWeight: 700 }}>{fmt(d.iva_retenido)}</td>
                  </tr>
                ) : (
                  <tr key={i}>
                    <td>{d.numero_documento || ""}</td>
                    <td>{d.fecha || ""}</td>
                    <td>{d.codigo_concepto || ""}</td>
                    <td className="pdv-right">{fmt(d.monto_documento)}</td>
                    <td className="pdv-right">{fmt(d.monto_objeto_retencion)}</td>
                    <td className="pdv-right">{fmt(d.tarifa_porcentaje, 0)}%</td>
                    <td className="pdv-right" style={{ fontWeight: 700 }}>{fmt(d.impuesto_retenido)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Móvil: tarjeta por item */}
          <div className="pdv-items-mobile">
            {detalle.map((d, i) => (
              <div key={i} className="pdv-item-card">
                <div className="pdv-item-head">
                  <span className="pdv-muted-label">{esIVA ? (d.numero_factura || d.numero_control) : d.numero_documento}</span>
                  <strong>{fmt(esIVA ? d.iva_retenido : d.impuesto_retenido)} Bs.</strong>
                </div>
                {esIVA ? (
                  <div className="pdv-item-meta">
                    <span>Base <strong>{fmt(d.base_imponible)}</strong></span>
                    <span>IVA <strong>{fmt(d.impuesto_iva)}</strong></span>
                    <span>Fecha <strong>{d.fecha_factura || "—"}</strong></span>
                  </div>
                ) : (
                  <div className="pdv-item-meta">
                    <span>Base <strong>{fmt(d.monto_objeto_retencion)}</strong></span>
                    <span>{fmt(d.tarifa_porcentaje, 0)}%</span>
                    <span>{d.codigo_concepto || ""}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Totales */}
        <div className="pdv-totales">
          {esIVA ? (
            <>
              <div className="pdv-total-row"><span>Compras con IVA</span><strong>{fmt(totales.total_compras_con_iva)} Bs.</strong></div>
              <div className="pdv-total-row"><span>Compras sin crédito</span><strong>{fmt(totales.compras_sin_derecho_credito)} Bs.</strong></div>
              <div className="pdv-total-row"><span>Base imponible</span><strong>{fmt(totales.base_imponible)} Bs.</strong></div>
              <div className="pdv-total-row"><span>Impuesto IVA</span><strong>{fmt(totales.impuesto_iva)} Bs.</strong></div>
              <div className="pdv-total-row pdv-total-final" style={{ borderTopColor: colorBanner }}>
                <span>IVA RETENIDO</span><strong style={{ color: colorBanner }}>{fmt(totales.iva_retenido)} Bs.</strong>
              </div>
            </>
          ) : (
            <>
              <div className="pdv-total-row"><span>Monto objeto retención</span><strong>{fmt(totales.monto_objeto_retencion)} Bs.</strong></div>
              <div className="pdv-total-row pdv-total-final" style={{ borderTopColor: colorBanner }}>
                <span>IMPUESTO RETENIDO</span><strong style={{ color: colorBanner }}>{fmt(totales.impuesto_retenido)} Bs.</strong>
              </div>
            </>
          )}
        </div>

        {/* Leyenda legal */}
        {(legal.providencia || legal.articulo || legal.leyenda_legal) && (
          <div className="pdv-legal">
            {legal.providencia && <div style={{ fontWeight: 600, marginBottom: 4 }}>{legal.providencia}</div>}
            {legal.articulo && <div>{legal.articulo}</div>}
            {/* (2026-07-18 A3/A4) Pie de imprenta (leyenda_legal del lote). */}
            {legal.leyenda_legal && <div style={{ marginTop: 4 }}>{legal.leyenda_legal}</div>}
          </div>
        )}

        <div className="pdv-actions">
          <button onClick={handleDownload} disabled={downloading} className="pdv-btn-pdf">
            {downloading ? "Generando PDF…" : "Descargar PDF"}
          </button>
          {error && <div className="pdv-error">{error}</div>}
        </div>

        <div className="pdv-footer">
          Documento emitido a través de <strong>Quantus Invoice</strong>.
          <br /><span className="pdv-muted-label">Powered by Sirius Solutions</span>
        </div>
      </div>

      <style>{PDV_STYLES_RET}</style>
    </div>
  );
};

// CSS compartido con PublicDispatchGuideView pero embebido aparte para
// independencia de import (cada componente trae su style block).
const PDV_STYLES_RET = `
.pdv-root { background:#f8fafc; min-height:100vh; color:#0f172a;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
.pdv-topbar { background:#0f172a; padding:12px 16px; display:flex; align-items:center; justify-content:center; gap:8px; }
.pdv-topbar-logo { height:28px; width:auto; }
.pdv-topbar-by { color:#cbd5e1; font-size:11px; letter-spacing:0.04em; }
.pdv-page { max-width:760px; margin:0 auto; padding:16px; }
.pdv-banner { padding:14px 18px; border-radius:8px; margin-bottom:14px;
  display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:8px; }
.pdv-banner-label { font-weight:700; letter-spacing:0.04em; }
.pdv-banner-numero { font-size:20px; font-weight:800; letter-spacing:0.04em; }
.pdv-legal { background:#fffbeb; border:1px solid #fde68a; color:#713f12;
  border-radius:8px; padding:12px 14px; font-size:12px; margin-bottom:14px; line-height:1.6; }
.pdv-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:14px; }
@media (max-width:600px) { .pdv-grid-2 { grid-template-columns:1fr; } }
.pdv-card { background:#fff; border:1px solid #e2e8f0; border-radius:8px; margin-bottom:14px; overflow:hidden; }
.pdv-card-title { background:#1e293b; color:#cbd5e1; padding:8px 14px;
  font-size:11px; font-weight:700; letter-spacing:0.08em; }
.pdv-card-body { padding:14px; font-size:13px; line-height:1.6; }
.pdv-strong { font-weight:700; font-size:14px; }
.pdv-muted { color:#64748b; }
.pdv-small { color:#475569; font-size:12px; }
.pdv-muted-label { color:#64748b; font-size:11px; letter-spacing:0.04em; text-transform:uppercase; }
.pdv-grid-3 { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.pdv-col-span-3 { grid-column:span 3; }
@media (max-width:600px) { .pdv-grid-3 { grid-template-columns:1fr 1fr; } .pdv-col-span-3 { grid-column:span 2; } }
.pdv-kv { display:flex; justify-content:space-between; gap:8px; padding:2px 0; }
.pdv-kv span { color:#64748b; font-size:12px; }
.pdv-kv strong { font-weight:600; }
.pdv-items-table { width:100%; border-collapse:collapse; font-size:12px; }
.pdv-items-table th { background:#f1f5f9; padding:8px 10px; text-align:left; font-weight:600; border-bottom:1px solid #e2e8f0; }
.pdv-items-table td { padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:top; }
.pdv-right { text-align:right; }
.pdv-empty { padding:16px; text-align:center; color:#94a3b8; font-style:italic; }
.pdv-items-mobile { display:none; padding:6px 10px 10px; }
@media (max-width:600px) { .pdv-items-desktop { display:none; } .pdv-items-mobile { display:block; } }
.pdv-item-card { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:10px 12px; margin-bottom:8px; }
.pdv-item-head { display:flex; justify-content:space-between; align-items:baseline; }
.pdv-item-meta { display:flex; gap:12px; flex-wrap:wrap; font-size:12px; color:#475569; margin-top:6px; }
.pdv-totales { background:#fff; border:1px solid #e2e8f0; border-radius:8px; padding:14px 16px; margin-bottom:14px; }
.pdv-total-row { display:flex; justify-content:space-between; padding:6px 0; font-size:14px; }
.pdv-total-row span { color:#475569; }
.pdv-total-final { margin-top:8px; padding-top:10px; border-top:2px solid #1e293b; font-size:17px; }
.pdv-actions { display:flex; flex-direction:column; align-items:center; gap:8px; margin:20px 0; }
.pdv-btn-pdf { background:#1e293b; color:#fff; border:none; cursor:pointer;
  padding:12px 28px; border-radius:8px; font-size:14px; font-weight:600;
  box-shadow:0 2px 6px rgba(0,0,0,0.12); transition:transform 120ms, background 120ms; }
.pdv-btn-pdf:hover:not(:disabled) { background:#0f172a; transform:translateY(-1px); }
.pdv-btn-pdf:disabled { opacity:0.6; cursor:wait; }
.pdv-error { color:#dc2626; font-size:12px; }
.pdv-footer { text-align:center; font-size:11px; color:#64748b; padding:16px;
  margin-top:16px; border-top:1px solid #e2e8f0; background:#fff; }
`;

export default PublicWithholdingView;
